import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserSetupStepKey } from '@brimo/shared';
import { api, apiUpload } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { sessionQueryKey } from '../../../lib/session';
import type {
  AcceptResponse,
  AvailabilitySuggestion,
  AvatarCrop,
  ExtensionCheck,
  Profile,
  ProfileField,
  ProfileSource,
  PublicInvitation,
  Signature,
  SignatureBody,
  UserChecklist,
  UserSetup,
} from './types';

/** Hooks of the user wizard, the personal checklist, profile/avatar/signature and the own billing (M8). */

export const userSetupKey = [...queryRoots.setup, 'user'] as const;
export const userChecklistKey = [...queryRoots.setup, 'checklist', 'user'] as const;
export const suggestionKey = [...queryRoots.setup, 'availability-suggestion'] as const;
export const profileKey = [...queryRoots.profile] as const;
export const signatureKey = [...queryRoots.profile, 'signature'] as const;

export function invitationKey(token: string) {
  return ['invitations', 'public', token] as const;
}

/** `GET /public/invitations/:token` — 404 unknown, 410 expired/revoked/accepted; not retried. */
export function usePublicInvitation(token: string) {
  return useQuery({
    queryKey: invitationKey(token),
    queryFn: () => api<PublicInvitation>(`/public/invitations/${encodeURIComponent(token)}`),
    retry: false,
    staleTime: Infinity,
  });
}

export interface AcceptBody {
  password?: string;
  passwordRepeat?: string;
  currentPassword?: string;
  consent: boolean;
}

export function useAcceptInvitation(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptBody) =>
      api<AcceptResponse>(`/public/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useUserSetup() {
  return useQuery({ queryKey: userSetupKey, queryFn: () => api<UserSetup>('/setup/user') });
}

/** „Weiter" (`continue`) / „Überspringen" (`skip`) of a step; the answer is the refreshed wizard. */
export function useStepAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: UserSetupStepKey; action: 'continue' | 'skip' }) =>
      api<UserSetup>(`/setup/user/steps/${input.key}`, { method: 'POST', body: { action: input.action } }),
    onSuccess: async (data) => {
      queryClient.setQueryData(userSetupKey, data);
      await queryClient.invalidateQueries({ queryKey: userChecklistKey });
    },
  });
}

export function useFinishSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<UserSetup>('/setup/user/finish', { method: 'POST', body: {} }),
    onSuccess: (data) => queryClient.setQueryData(userSetupKey, data),
  });
}

export function useAvailabilitySuggestion() {
  return useQuery({
    queryKey: suggestionKey,
    queryFn: () => api<AvailabilitySuggestion>('/setup/user/availability-suggestion'),
  });
}

export function useUserChecklist(enabled = true) {
  return useQuery({
    queryKey: userChecklistKey,
    queryFn: () => api<UserChecklist>('/setup/checklist/user'),
    enabled,
  });
}

export function useHideChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<UserChecklist>('/setup/checklist/user/hide', { method: 'POST', body: {} }),
    onSuccess: async (data) => {
      queryClient.setQueryData(userChecklistKey, data);
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useProfile() {
  return useQuery({ queryKey: profileKey, queryFn: () => api<Profile>('/settings/profile') });
}

export type ProfilePatch = Partial<Record<ProfileField, string | null>>;

/** Per-field auto-save (`PATCH /settings/profile`); the answer replaces the cached profile. */
export function usePatchProfile(source: ProfileSource) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfilePatch) =>
      api<Profile>('/settings/profile', { method: 'PATCH', body: { ...patch, source } }),
    onSuccess: async (data) => {
      queryClient.setQueryData(profileKey, data);
      await queryClient.invalidateQueries({ queryKey: signatureKey });
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

/** Live hint of the extension field (`GET /settings/profile/extension`). */
export function checkExtension(value: string, source: ProfileSource): Promise<ExtensionCheck> {
  const query = new URLSearchParams({ value, source });
  return api<ExtensionCheck>(`/settings/profile/extension?${query.toString()}`);
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { file: File; crop: AvatarCrop | null }) => {
      const form = new FormData();
      if (input.crop) {
        form.append('x', String(input.crop.x));
        form.append('y', String(input.crop.y));
        form.append('size', String(input.crop.size));
      }
      form.append('file', input.file);
      return apiUpload<Profile>('/settings/profile/avatar', form);
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(profileKey, data);
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useCropAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (crop: AvatarCrop) =>
      api<Profile>('/settings/profile/avatar', { method: 'PATCH', body: crop }),
    onSuccess: (data) => queryClient.setQueryData(profileKey, data),
  });
}

export function useDeleteAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<Profile>('/settings/profile/avatar', { method: 'DELETE' }),
    onSuccess: async (data) => {
      queryClient.setQueryData(profileKey, data);
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

export function useSignature() {
  return useQuery({ queryKey: signatureKey, queryFn: () => api<Signature>('/settings/signature') });
}

export function useSaveSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SignatureBody) => api<Signature>('/settings/signature', { method: 'PUT', body }),
    onSuccess: async (data) => {
      queryClient.setQueryData(signatureKey, data);
      await queryClient.invalidateQueries({ queryKey: queryRoots.setup });
    },
  });
}
